import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TimeLogSavedDialog from "./TimeLogSavedDialog";
import { fetchTimeLogSavedSummary } from "./timeLogSavedApi";
import {
  buildSavedHeader,
  groupSavesByProject,
  mapTimeLogSavedSummary,
  savesFromPayloads,
} from "../../utils/timeLogSaved";

const TimeLogSavedContext = createContext({ notifyTimeLogSaved: () => Promise.resolve() });

/**
 * Anything that saves time logs calls this once they are saved:
 *
 *   const { notifyTimeLogSaved } = useTimeLogSaved();
 *   notifyTimeLogSaved(payload);                 // one log
 *   notifyTimeLogSaved(payloads, { onAddAnother }); // several (one popup for the whole batch)
 *
 * It looks up the utilisation of every project in the batch and opens ONE popup. It never throws
 * and never delays the caller: if the lookup fails the user simply does not get the popup.
 * Outside the provider it does nothing, so components that use it work in isolation too.
 */
export const useTimeLogSaved = () => useContext(TimeLogSavedContext);

export const TimeLogSavedProvider = ({ children }) => {
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const latestRequest = useRef(0);

  const notifyTimeLogSaved = useCallback(async (payloads, { onAddAnother } = {}) => {
    try {
      const saves = savesFromPayloads(payloads);
      if (!saves.length) return;

      const request = ++latestRequest.current;
      const groups = groupSavesByProject(saves);
      const results = await Promise.allSettled(groups.map((group) => fetchTimeLogSavedSummary(group.projectId)));
      if (request !== latestRequest.current) return; // a newer save has taken over

      const projects = results.flatMap((result, index) =>
        result.status === "fulfilled" ? [mapTimeLogSavedSummary(result.value, groups[index])] : []
      );
      if (!projects.length) return;

      const shown = new Set(projects.map((project) => Number(project.projectId)));
      const shownSaves = saves.filter((save) => shown.has(save.projectId));
      setPopup({ summary: buildSavedHeader(shownSaves), projects, onAddAnother });
    } catch (error) {
      console.warn("Could not show the time log summary:", error);
    }
  }, []);

  const close = useCallback(() => setPopup(null), []);

  const value = useMemo(() => ({ notifyTimeLogSaved }), [notifyTimeLogSaved]);

  return (
    <TimeLogSavedContext.Provider value={value}>
      {children}
      {popup && (
        <TimeLogSavedDialog
          open
          summary={popup.summary}
          projects={popup.projects}
          onClose={close}
          onSkip={close}
          onAddAnother={
            popup.onAddAnother
              ? () => {
                  close();
                  popup.onAddAnother();
                }
              : undefined
          }
          onViewBudget={(projectId) => {
            close();
            localStorage.setItem("project_id", projectId);
            navigate(`/projects/projectDetail/${projectId}/budget`);
          }}
        />
      )}
    </TimeLogSavedContext.Provider>
  );
};

export default TimeLogSavedProvider;
